/**
 * Logger utility for SmartShield application
 * Provides structured logging with levels and optional remote logging
 */

import { jwtDecode } from 'jwt-decode';

const LOG_LEVELS = {
  TRACE: 0,
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  SILENT: 5,
};

const globalObject = typeof globalThis === 'object' && globalThis !== null ? globalThis : undefined;

const logDebug = (message, error) => {
  const debug = globalObject?.console?.debug;
  if (typeof debug === 'function') {
    debug.call(globalObject.console, message, error);
  }
};

const isMeaningfulString = (value) =>
  typeof value === 'string' && value.trim() !== '' && value !== 'null' && value !== 'undefined';

const sanitizeString = (value) => {
  if (!isMeaningfulString(value)) {
    return null;
  }
  return value.trim();
};

const extractUsernameFromDecodedToken = (decoded) => {
  if (!decoded || typeof decoded !== 'object') {
    return null;
  }
  const candidate =
    decoded.preferred_username || decoded.username || decoded.email || decoded.name || decoded.preferredUsername;

  return sanitizeString(candidate);
};

const extractUsernameFromKeycloakToken = (keycloakInstance) => {
  const token = keycloakInstance?.token;
  if (!token) {
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    return extractUsernameFromDecodedToken(decoded);
  } catch (error) {
    logDebug('Failed to decode Keycloak token', error);
    return null;
  }
};

const extractUsernameFromUserEntry = (rawUser) => {
  const sanitized = sanitizeString(rawUser);
  if (!sanitized) {
    return null;
  }

  try {
    const parsed = JSON.parse(sanitized);
    if (typeof parsed === 'string') {
      return sanitizeString(parsed);
    }
    if (parsed && typeof parsed === 'object') {
      return sanitizeString(parsed.email || parsed.username || parsed.name || parsed.preferred_username);
    }
    return null;
  } catch (error) {
    logDebug('Unable to parse user session entry', error);
    return sanitized;
  }
};

const extractUsernameFromSessionStorage = (storage) => {
  if (!storage) {
    return null;
  }

  const fromUserEntry = extractUsernameFromUserEntry(storage.getItem('user'));
  if (fromUserEntry) {
    return fromUserEntry;
  }

  return sanitizeString(storage.getItem('fullname'));
};

class Logger {
  constructor() {
    this.level = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
    this.enableRemoteLogging = process.env.REACT_APP_ENABLE_REMOTE_LOGGING === 'true';
    this.remoteEndpoint = process.env.REACT_APP_LOG_ENDPOINT || '';
    this.clientIp = null;
    this.ipFetchPromise = null;
    // Disable external IP fallback by default; allows enabling explicitly if needed
    const envFlag = process.env.REACT_APP_ENABLE_IP_FETCH;
    this.enableIpFetch = envFlag === 'true';
  }

  /**
   * Get Keycloak username from token or sessionStorage
   * Returns empty string if not available
   */
  getKeycloakUsername() {
    const keycloakInstance = globalObject?.keycloak;
    const fromToken = extractUsernameFromKeycloakToken(keycloakInstance);
    if (fromToken) {
      return fromToken;
    }

    const storage = globalObject?.sessionStorage;
    const fromSession = extractUsernameFromSessionStorage(storage);
    return fromSession || '';
  }

  /**
   * Get client IP address
   * Tries multiple methods to get IP information
   */
  async getClientIp() {
    // Return cached IP if available
    if (this.clientIp) {
      return this.clientIp;
    }

    // If already fetching, return the promise
    if (this.ipFetchPromise !== null) {
      return this.ipFetchPromise;
    }

    // Try to get IP from various sources
    const fetchPromise = this.fetchClientIp();
    this.ipFetchPromise = fetchPromise;
    this.clientIp = await fetchPromise;
    this.ipFetchPromise = null;
    return this.clientIp;
  }

  async fetchClientIp() {
    if (!this.enableIpFetch) {
      return null;
    }

    const fetchFn =
      (globalObject && typeof globalObject.fetch === 'function' && globalObject.fetch.bind(globalObject)) ||
      (typeof fetch === 'function' ? fetch : null);

    if (!fetchFn) {
      return null;
    }

    const strategies = [
      () => this.fetchIpFromBackend(fetchFn),
      () => this.fetchIpFromExternal(fetchFn, 'https://api.ipify.org?format=json'),
      () => this.fetchIpFromExternal(fetchFn, 'https://api64.ipify.org?format=json'),
    ];

    for (const strategy of strategies) {
      try {
        const result = await strategy();
        if (result) {
          return result;
        }
      } catch (error) {
        logDebug('IP fetch strategy failed', error);
      }
    }

    return null; // Return null if IP cannot be determined
  }

  async fetchIpFromBackend(fetchFn) {
    const apiBase =
      globalObject?.REACT_APP_API_PURE ||
      globalObject?.process?.env?.REACT_APP_API_PURE ||
      (typeof process !== 'undefined' ? process.env?.REACT_APP_API_PURE : '') ||
      '';

    if (!isMeaningfulString(apiBase)) {
      return null;
    }

    try {
      const response = await fetchFn(`${apiBase}user/ip`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response || typeof response.ok !== 'boolean') {
        return null;
      }
      if (response.ok) {
        const data = await response.json();
        return sanitizeString(data?.ip);
      }
    } catch (error) {
      logDebug('Failed to fetch client IP from backend', error);
    }

    return null;
  }

  async fetchIpFromExternal(fetchFn, url) {
    if (!this.enableIpFetch) {
      return null;
    }

    try {
      const response = await fetchFn(url, {
        method: 'GET',
        cache: 'no-cache',
      });
      if (!response || typeof response.ok !== 'boolean') {
        return null;
      }
      if (response.ok) {
        const data = await response.json();
        return sanitizeString(data?.ip);
      }
    } catch (error) {
      logDebug(`Failed to fetch IP from external service ${url}`, error);
    }

    return null;
  }

  /**
   * Set client IP manually (useful if you get it from API responses)
   * Only sets if IP is valid, otherwise sets to null
   */
  setClientIp(ip) {
    // Only set if IP is valid (not empty, not 'unknown')
    if (ip && ip.trim() !== '' && ip !== 'unknown') {
      this.clientIp = ip.trim();
    } else {
      this.clientIp = null;
    }
  }

  setLevel(level) {
    this.level = level;
  }

  shouldLog(level) {
    return level >= this.level;
  }

  /**
   * Get current timestamp in GMT+7 formatted as ISO string with offset
   */
  getTimestampGMT7() {
    const now = new Date();
    // getTimezoneOffset returns minutes behind UTC (e.g., -420 for GMT+7)
    const localOffsetMinutes = now.getTimezoneOffset();
    const targetOffsetMinutes = 7 * 60; // GMT+7 in minutes
    const diffMinutes = targetOffsetMinutes + localOffsetMinutes;
    const gmt7Date = new Date(now.getTime() + diffMinutes * 60000);
    const isoString = gmt7Date.toISOString();
    return isoString.replace('Z', '+07:00');
  }

  formatMessage(level, message, ...args) {
    const timestamp = this.getTimestampGMT7();
    const levelName = Object.keys(LOG_LEVELS).find((key) => LOG_LEVELS[key] === level);
    const currentUrl = globalObject?.location?.href ?? 'N/A';
    const userAgent = globalObject?.navigator?.userAgent ?? 'N/A';
    const logData = {
      timestamp,
      level: levelName,
      message,
      args: args.length > 0 ? args : undefined,
      url: currentUrl,
      userAgent,
    };

    // Add IP if available (empty string if not available)
    logData.clientIp = this.clientIp || '';

    // Add Keycloak username if available (empty string if not available)
    logData.username = this.getKeycloakUsername();

    return logData;
  }

  /**
   * Get client IP synchronously (returns cached value or null)
   */
  getClientIpSync() {
    return this.clientIp || null;
  }

  async sendToRemote(logData) {
    if (!this.enableRemoteLogging || !this.remoteEndpoint) {
      return;
    }

    try {
      // Only send errors and warnings to remote in production
      if (logData.level === 'ERROR' || logData.level === 'WARN') {
        // Try to get IP if not already in logData
        if (!logData.clientIp) {
          try {
            const ip = await this.getClientIp();
            logData.clientIp = ip || ''; // Empty string if IP not available
          } catch (error) {
            // Silently fail IP fetch, set to empty string
            logData.clientIp = '';
            logDebug('Failed to obtain client IP during remote logging', error);
          }
        }

        // Ensure username is included (get fresh value)
        if (!logData.username) {
          logData.username = this.getKeycloakUsername();
        }

        await fetch(this.remoteEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(logData),
        });
      }
    } catch (error) {
      // Silently fail remote logging to avoid infinite loops
      logDebug('Failed to send log to remote endpoint', error);
    }
  }

  trace(message, ...args) {
    if (this.shouldLog(LOG_LEVELS.TRACE)) {
      const logData = this.formatMessage(LOG_LEVELS.TRACE, message, ...args);
      console.trace(message, ...args);
      this.sendToRemote(logData);
    }
  }

  debug(message, ...args) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      const logData = this.formatMessage(LOG_LEVELS.DEBUG, message, ...args);
      console.debug(message, ...args);
      this.sendToRemote(logData);
    }
  }

  info(message, ...args) {
    if (this.shouldLog(LOG_LEVELS.INFO)) {
      const logData = this.formatMessage(LOG_LEVELS.INFO, message, ...args);
      console.info(message, ...args);
      this.sendToRemote(logData);
    }
  }

  warn(message, ...args) {
    if (this.shouldLog(LOG_LEVELS.WARN)) {
      const logData = this.formatMessage(LOG_LEVELS.WARN, message, ...args);
      console.warn(message, ...args);
      this.sendToRemote(logData);
    }
  }

  error(message, error, ...args) {
    if (this.shouldLog(LOG_LEVELS.ERROR)) {
      const logData = this.formatMessage(LOG_LEVELS.ERROR, message, error, ...args);

      // Enhanced error logging
      if (error instanceof Error) {
        logData.error = {
          name: error.name,
          message: error.message,
          stack: error.stack,
        };
        console.error(message, error, ...args);
      } else if (error !== undefined) {
        console.error(message, error, ...args);
      } else {
        console.error(message, ...args);
      }

      this.sendToRemote(logData);
    }
  }

  // Helper method for API errors
  apiError(context, error, url) {
    const errorInfo = {
      context,
      url,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      data: error?.response?.data,
      message: error?.message,
    };

    // Try to extract IP from error response headers if available
    if (error?.response?.headers) {
      const ipHeaders = [
        'x-forwarded-for',
        'x-real-ip',
        'cf-connecting-ip', // Cloudflare
        'x-client-ip',
      ];

      for (const header of ipHeaders) {
        const ip = error.response.headers.get?.(header) || error.response.headers[header];
        if (ip) {
          // X-Forwarded-For can contain multiple IPs, take the first one
          const clientIp = ip.split(',')[0].trim();
          // Only set if valid IP (not empty, not 'unknown')
          if (clientIp && clientIp !== 'unknown') {
            this.setClientIp(clientIp);
            errorInfo.clientIp = clientIp;
            break;
          }
        }
      }
    }

    this.error(`API Error in ${context}`, error, errorInfo);
  }
}

// Export singleton instance
const logger = new Logger();

// Initialize IP fetch on load (non-blocking)
if (globalObject) {
  // Fetch IP in background, don't wait for it
  logger.getClientIp().catch(() => {
    // Silently fail, IP will be null
  });
}

export default logger;
export { Logger };
